import React, { useState, useEffect } from 'react';
import { Container, Card, Alert, Table, Image, Badge, Spinner } from 'react-bootstrap';
import { Link, useParams, useLocation } from 'react-router-dom';
import { getCastaways } from '../api/castaways';
import { Castaway, Draft, Member } from '../types';
import { scoreDraftByLineup, activeCastawayIdsForEpisode } from '../scoring';
import TribeBadge, { getTribeColor } from './TribeBadge';
import './LeagueStandings.css';
import '../App.css';

interface LeagueDrafts {
  [userName: string]: Draft;
}

interface EpisodeScore {
  tribeImmunityWins: number;
  tribeRewardWins: number;
  individualRewardWins: number;
  rightSideOfVote: number;
  individualImmunityWins: number;
  votesAgainst: number;
  idolObtained: number;
  advantageObtained: number;
  playedIdolAdvantage: number;
  advantageAbandoned: number;
  idolAbandoned: number;
  eliminationPick: number;
  shotInDarkWin: number;
  coinFlipWin?: number;
  finalPlacement: number;
  votedOut: boolean;
  involuntaryEvacuation?: boolean;
  voluntaryExit?: boolean;
}

interface AllScores {
  [episode: string]: {
    [castawayId: string]: EpisodeScore;
  };
}

interface EliminationPicks {
  [userName: string]: {
    [episode: string]: number;
  };
}

const calculateScore = (castawayId: number, allScores: AllScores, episodeRange?: { start?: number, end?: number }): number => {
  let totalScore = 0;
  Object.entries(allScores).forEach(([episode, episodeScores]) => {
    const episodeNum = parseInt(episode.replace('episode', ''), 10);
    // Episode 1 is pre-draft events only (no fantasy points)
    if (episodeNum === 1) return;
    if (episodeRange) {
      if (episodeRange.start && episodeNum < episodeRange.start) return;
      if (episodeRange.end && episodeNum > episodeRange.end) return;
    }
    const scores = episodeScores[castawayId];
    if (scores) {
      totalScore += scores.tribeImmunityWins * 1;
      totalScore += scores.tribeRewardWins * 1;
      totalScore += scores.individualRewardWins * 2;
      totalScore += scores.rightSideOfVote * 1;
      totalScore += scores.individualImmunityWins; // This is already points
      totalScore += scores.votesAgainst * -0.5;
      totalScore += scores.idolObtained * 4;
      totalScore += scores.advantageObtained * 2;
      totalScore += scores.playedIdolAdvantage * 2;
      totalScore += scores.advantageAbandoned * -2;
      totalScore += scores.idolAbandoned * -3;
      totalScore += scores.shotInDarkWin * 4;
      totalScore += (scores.coinFlipWin || 0) * 4;

      if (scores.finalPlacement === 1) totalScore += 25;
      if (scores.finalPlacement === 2) totalScore += 10;
      if (scores.finalPlacement === 3) totalScore += 5;
    }
  });
  return totalScore;
};

const LeagueStandings: React.FC = () => {
  const [allCastaways, setAllCastaways] = useState<Castaway[]>([]);
  const { leagueId } = useParams<{ leagueId: string }>();
  const location = useLocation();
  const [drafts, setDrafts] = useState<LeagueDrafts | null>(null);
  const [allScores, setAllScores] = useState<AllScores>({});
  const [eliminationPicks, setEliminationPicks] = useState<EliminationPicks>({});
  const [leagueMembers, setLeagueMembers] = useState<Member[]>([]);
  const [tribes, setTribes] = useState<{ name: string, color: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [leagueName, setLeagueName] = useState<string>('');
  const [leagueSeason, setLeagueSeason] = useState<number>(49);
  const [rosterSize, setRosterSize] = useState<number>(3);
  const [activeRosterSize, setActiveRosterSize] = useState<number>(3);
  const [currentEpisode, setCurrentEpisode] = useState<number>(1);
  const [selectedSeason, setSelectedSeason] = useState<number>(() => {
    return parseInt(localStorage.getItem('selectedSeason') || '49');
  });
  const [viewingUser, setViewingUser] = useState<{ survivorName?: string, userName: string } | null>(null);

  useEffect(() => {
    const handleSeasonChange = () => {
      setSelectedSeason(parseInt(localStorage.getItem('selectedSeason') || '49'));
    };
    window.addEventListener('seasonChanged', handleSeasonChange);
    return () => window.removeEventListener('seasonChanged', handleSeasonChange);
  }, []);

  useEffect(() => {
    const userStr = localStorage.getItem(`fantasyUser_${selectedSeason}`);
    if (userStr) {
      try {
        setViewingUser(JSON.parse(userStr));
      } catch (e) {
        setViewingUser(null);
      }
    } else {
      setViewingUser(null);
    }
  }, [selectedSeason]);

  useEffect(() => {
    if (leagueId && !error) {
      const loadCastaways = async () => {
        setIsLoading(true);
        try {
          const castaways = await getCastaways(leagueId);
          setAllCastaways(castaways);
        } catch (err) {
          setError('Could not load castaway data.');
        } finally {
          setIsLoading(false);
        }
      };
      loadCastaways();
    } else if (error) {
        setAllCastaways([]);
    }
  }, [leagueId, error, selectedSeason]);
  
  const getCastawayById = (id: number) => allCastaways.find(c => c.id === id);

  useEffect(() => {
    document.title = leagueName ? `${leagueName} Standings` : 'League Standings';
  }, [leagueName]);

  useEffect(() => {
    const fetchData = async () => {
      if (!leagueId) {
        setError("No League ID provided.");
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const [leagueResponse, scoresResponse, eliminationPicksResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/leagues/all`),
          fetch(`${process.env.REACT_APP_API_URL}/api/scores/${selectedSeason}`),
          fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${leagueId}/elimination-picks`)
        ]);

        if (!leagueResponse.ok) throw new Error('Failed to fetch league data');
        if (!scoresResponse.ok) throw new Error('Failed to fetch scores');
        if (!eliminationPicksResponse.ok) throw new Error('Failed to fetch elimination picks');

        const leagueData = await leagueResponse.json();
        const currentLeague = leagueData.leagues.find((l: any) => l.id === leagueId);

        if (!currentLeague) throw new Error('League not found');
        
        const currentSeason = currentLeague.season || 49;
        if (currentSeason !== selectedSeason) {
            setError(`Season ${selectedSeason} not yet configured.`);
            setIsLoading(false);
            return;
        }
        
        const currentRosterSize = currentLeague.rosterSize || (currentSeason >= 51 ? 5 : 3);
        const currentActiveRosterSize = currentLeague.activeRosterSize || 3;

        setLeagueName(currentLeague.name);
        setLeagueSeason(currentSeason);
        setRosterSize(currentRosterSize);
        setActiveRosterSize(currentActiveRosterSize);
        setLeagueMembers(currentLeague.members || []);
        setTribes(currentLeague.tribes || []);

        const scoresData = await scoresResponse.json();
        const loadedScores = scoresData.scores || {};
        setAllScores(loadedScores);

        // Calculate current active episode
        const nextEpisodeEntry = (Object.entries(currentLeague.lineupDeadlines || {}) as [string, string][])
          .filter(([, deadline]) => new Date(deadline).getTime() > Date.now())
          .sort(([a], [b]) => Number(a) - Number(b))[0];
        const activeEpisode = nextEpisodeEntry ? Number(nextEpisodeEntry[0]) : (Object.keys(loadedScores).length > 0 ? Math.max(...Object.keys(loadedScores).map(Number)) : 1);
        setCurrentEpisode(activeEpisode);

        const eliminationPicksData = await eliminationPicksResponse.json();
        setEliminationPicks(eliminationPicksData.eliminationPicks || {});

        // Fetch drafts separately
        const draftsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${leagueId}/drafts`);
        if (!draftsResponse.ok) {
            const errData = await draftsResponse.json();
            throw new Error(errData.message || 'Failed to fetch drafts');
        }
        const draftsData = await draftsResponse.json();
        // Backward compatibility for old data
        for (const userName in draftsData.drafts) {
          const draft = draftsData.drafts[userName];
          if (draft.previousTeam && !draft.originalTeam) {
            draft.originalTeam = draft.previousTeam;
            delete draft.previousTeam;
          }
        }
        setDrafts(draftsData.drafts);

      } catch (err: any) {
        setError(err.message || 'An error occurred');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [leagueId, selectedSeason]);

  // Handle scrolling to specific user row when data loads
  useEffect(() => {
    if (!isLoading && drafts && location.hash) {
      const id = decodeURIComponent(location.hash.replace('#', ''));
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.style.transition = 'background-color 1s ease-in-out';
          element.style.backgroundColor = '#fff3cd';
          setTimeout(() => { element.style.backgroundColor = ''; }, 2000);
        }
      }, 300);
    }
  }, [isLoading, drafts, location.hash]);

  const calculateTeamScore = (userName: string, draft: Draft) => {
    const { team = [] } = draft;
    
    if ((rosterSize > 3 || leagueSeason >= 51) && draft.lineupHistory) {
      const totalScore = scoreDraftByLineup(
        draft,
        allScores,
        (castawayId, episodeScores) => calculateScore(castawayId, { episode: episodeScores }),
        rosterSize,
      );
      return { totalScore };
    }

    const totalScore = team.reduce((acc, castawayId) => {
      const score = calculateScore(castawayId, allScores);
      return acc + score;
    }, 0);

    return { totalScore };
  };

  const calculateEliminationPickPoints = (userName: string) => {
    let eliminationPoints = 0;
    const userPicks = eliminationPicks[userName];
    if (userPicks) {
      Object.entries(userPicks).forEach(([episode, pickedCastawayId]) => {
        const episodeScores = allScores[episode];
        if (episodeScores) {
          const isPickedVotedOut = Object.keys(episodeScores).some(castawayId => 
            episodeScores[castawayId].votedOut && Number(castawayId) === pickedCastawayId
          );
          if (isPickedVotedOut) {
            eliminationPoints += 3;
          }
        }
      });
    }
    return eliminationPoints;
  };

  const getSurvivorBadgeColor = (count: number) => {
    if (count >= 3) return 'success';
    if (count === 2) return 'info';
    if (count === 1) return 'warning';
    return 'danger';
  };

  const renderRankBadge = (rank: number) => {
    if (rank === 1) return <Badge className="rank-badge-gold px-2 py-1">1st 🏆</Badge>;
    if (rank === 2) return <Badge className="rank-badge-silver px-2 py-1">2nd 🥈</Badge>;
    if (rank === 3) return <Badge className="rank-badge-bronze px-2 py-1">3rd 🥉</Badge>;
    return <Badge bg="light" text="dark" className="border px-2 py-1">#{rank}</Badge>;
  };

  const renderCastawayItem = (castaway: Castaway, isBench = false) => {
    const castawayScore = calculateScore(castaway.id, allScores);
    const isVotedOut = Object.values(allScores).some(ep => ep[castaway.id]?.votedOut);
    const isEvacuated = Object.values(allScores).some(ep => ep[castaway.id]?.involuntaryEvacuation);
    const isVoluntaryExit = Object.values(allScores).some(ep => ep[castaway.id]?.voluntaryExit);
    const finalPlacement = Object.values(allScores).reduce((acc, ep) => {
      const p = ep[castaway.id]?.finalPlacement;
      return (p && p > 0) ? p : acc;
    }, 0);
    const outOfGameClass = isVoluntaryExit ? 'voted-out' : isVotedOut ? 'voted-out' : isEvacuated ? 'evacuated' : '';
    const tribeColor = getTribeColor(castaway.tribe, tribes, castaway.tribeColor);

    return (
      <div key={castaway.id} className={`castaway-mini-card ${isBench ? 'opacity-75' : ''}`}>
        <Link to={`/league/${leagueId}/castaway/${castaway.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className={`castaway-container ${outOfGameClass}`}>
            <Image
              src={castaway.imageUrl}
              roundedCircle
              referrerPolicy="no-referrer"
              style={{
                width: '46px',
                height: '46px',
                objectFit: 'cover',
                border: `3px solid ${tribeColor}`,
                boxShadow: isBench ? 'none' : '0 2px 6px rgba(0,0,0,0.12)'
              }}
              title={`${castaway.name} (${castawayScore} pts) - Click for details`}
            />
            {finalPlacement > 0 && (
              <div style={{ position: 'absolute', top: -5, left: -5, zIndex: 10 }}>
                {finalPlacement === 1 && <Badge bg="warning" text="dark" style={{ border: '1px solid gold', fontSize: '0.65rem' }}>1st</Badge>}
                {finalPlacement === 2 && <Badge bg="secondary" style={{ border: '1px solid silver', fontSize: '0.65rem' }}>2nd</Badge>}
                {finalPlacement === 3 && <Badge bg="danger" style={{ border: '1px solid #cd7f32', backgroundColor: '#cd7f32', fontSize: '0.65rem' }}>3rd</Badge>}
              </div>
            )}
          </div>
          <div className="castaway-name-label" title={castaway.name}>{castaway.name}</div>
          <TribeBadge tribeName={castaway.tribe} tribes={tribes} tribeColor={castaway.tribeColor} style={{ fontSize: '0.6rem', padding: '1px 3px' }} className="mb-1 d-inline-block" /><br />
          <Badge bg={isBench ? "secondary" : "success"} style={{ fontSize: '0.68rem' }}>{castawayScore} pts</Badge>
        </Link>
      </div>
    );
  };

  const memberUserNames = new Set(leagueMembers.map(m => m.userName));
  const isNewRules = rosterSize > 3 || leagueSeason >= 51;

  return (
    <Container className="mt-5 mb-5">
      <Card className="shadow-sm home-card">
        <Card.Header as="h4" className="card-title-custom text-center">
          League Standings - Season {selectedSeason} {viewingUser ? `(${viewingUser.survivorName || viewingUser.userName})` : ''}
        </Card.Header>
        <Card.Body>
          <div className="text-center mb-4">
            <Link to="/" className="btn btn-secondary">Back to Home</Link>
          </div>
          {isLoading && <div className="text-center"><Spinner animation="border" /></div>}
          {error && <Alert variant="danger">{error}</Alert>}
          {drafts && Object.keys(drafts).length > 0 ? (
            <>
              {/* DESKTOP TABLE VIEW */}
              <div className="d-none d-md-block table-responsive">
                <Table striped bordered hover className="standings-table align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '80px', textAlign: 'center' }}>Rank</th>
                      <th style={{ minWidth: '150px' }}>Player</th>
                      {isNewRules ? (
                        <>
                          <th style={{ minWidth: '240px' }}>
                            <div className="d-flex align-items-center gap-1">
                              <span>Active Lineup</span>
                              <Badge bg="success" style={{ fontSize: '0.7rem' }}>Live {activeRosterSize}</Badge>
                            </div>
                          </th>
                          <th style={{ minWidth: '180px' }}>
                            <div className="d-flex align-items-center gap-1">
                              <span>Bench</span>
                              <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>Reserve {rosterSize - activeRosterSize}</Badge>
                            </div>
                          </th>
                        </>
                      ) : (
                        <th style={{ minWidth: '260px' }}>Drafted Team & Scores</th>
                      )}
                      <th style={{ width: '130px', textAlign: 'center' }}>Elimination Picks</th>
                      <th style={{ width: '120px', textAlign: 'center' }}>Survivors Left</th>
                      <th style={{ width: '110px', textAlign: 'center' }}>Total Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(drafts)
                      .filter(([userName]) => memberUserNames.has(userName))
                      .map(([userName, draft]) => {
                        const { totalScore } = calculateTeamScore(userName, draft);
                        const eliminationPickPoints = calculateEliminationPickPoints(userName);
                        const activeIds = isNewRules
                          ? activeCastawayIdsForEpisode(draft, currentEpisode, rosterSize)
                          : (draft.team || []);
                        const activeCastaways = activeIds.map(id => getCastawayById(id)).filter(Boolean) as Castaway[];
                        const benchCastaways = isNewRules
                          ? (draft.team || []).filter(id => !activeIds.includes(id)).map(id => getCastawayById(id)).filter(Boolean) as Castaway[]
                          : [];

                        return {
                          userName,
                          draft,
                          activeCastaways,
                          benchCastaways,
                          totalScore: totalScore + eliminationPickPoints,
                          eliminationPickPoints,
                          survivorsRemaining: draft.team.filter(castawayId => {
                            const isOutOfGame = Object.values(allScores).some(episodeScores => 
                              episodeScores[castawayId] && (episodeScores[castawayId].votedOut || episodeScores[castawayId].involuntaryEvacuation || episodeScores[castawayId].voluntaryExit)
                            );
                            return !isOutOfGame;
                          }).length,
                        };
                      })
                      .sort((a, b) => b.totalScore - a.totalScore)
                      .map((item, index) => {
                        const isCurrentUser = viewingUser && viewingUser.userName === item.userName;
                        return (
                          <tr key={item.userName} id={item.userName} className={isCurrentUser ? 'table-primary fw-bold' : ''}>
                            <td className="text-center">{renderRankBadge(index + 1)}</td>
                            <td>
                              <strong>{item.userName}</strong>
                              {isCurrentUser && <Badge bg="primary" className="ms-2">You</Badge>}
                            </td>
                            {isNewRules ? (
                              <>
                                <td>
                                  <div className="d-flex flex-wrap gap-2 align-items-center">
                                    {item.activeCastaways.map(c => renderCastawayItem(c, false))}
                                    {item.activeCastaways.length === 0 && <span className="text-muted small">No active players</span>}
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex flex-wrap gap-2 align-items-center">
                                    {item.benchCastaways.map(c => renderCastawayItem(c, true))}
                                    {item.benchCastaways.length === 0 && <span className="text-muted small">No bench players</span>}
                                  </div>
                                </td>
                              </>
                            ) : (
                              <td>
                                <div className="d-flex flex-wrap gap-2 align-items-center">
                                  {item.activeCastaways.map(c => renderCastawayItem(c, false))}
                                </div>
                              </td>
                            )}
                            <td className="text-center">
                              <Badge bg="info" className="text-dark fs-6">+{item.eliminationPickPoints}</Badge>
                            </td>
                            <td className="text-center">
                              <Badge bg={getSurvivorBadgeColor(item.survivorsRemaining)} className="fs-6">
                                {item.survivorsRemaining}/{rosterSize}
                              </Badge>
                            </td>
                            <td className="text-center">
                              <Badge bg="primary" className="fs-6 px-3 py-2">{item.totalScore}</Badge>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </Table>
              </div>

              {/* MOBILE CARD VIEW */}
              <div className="d-block d-md-none">
                {Object.entries(drafts)
                  .filter(([userName]) => memberUserNames.has(userName))
                  .map(([userName, draft]) => {
                    const { totalScore } = calculateTeamScore(userName, draft);
                    const eliminationPickPoints = calculateEliminationPickPoints(userName);
                    const activeIds = isNewRules
                      ? activeCastawayIdsForEpisode(draft, currentEpisode, rosterSize)
                      : (draft.team || []);
                    const activeCastaways = activeIds.map(id => getCastawayById(id)).filter(Boolean) as Castaway[];
                    const benchCastaways = isNewRules
                      ? (draft.team || []).filter(id => !activeIds.includes(id)).map(id => getCastawayById(id)).filter(Boolean) as Castaway[]
                      : [];

                    return {
                      userName,
                      draft,
                      activeCastaways,
                      benchCastaways,
                      totalScore: totalScore + eliminationPickPoints,
                      eliminationPickPoints,
                      survivorsRemaining: draft.team.filter(castawayId => {
                        const isOutOfGame = Object.values(allScores).some(episodeScores => 
                          episodeScores[castawayId] && (episodeScores[castawayId].votedOut || episodeScores[castawayId].involuntaryEvacuation)
                        );
                        return !isOutOfGame;
                      }).length,
                    };
                  })
                  .sort((a, b) => b.totalScore - a.totalScore)
                  .map((item, index) => {
                    const isCurrentUser = viewingUser && viewingUser.userName === item.userName;
                    return (
                      <div
                        key={item.userName}
                        id={`mobile-${item.userName}`}
                        className={`standings-mobile-card ${isCurrentUser ? 'is-current-user' : ''}`}
                      >
                        <div className="standings-mobile-header d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center gap-2">
                            {renderRankBadge(index + 1)}
                            <strong className="fs-6">{item.userName}</strong>
                            {isCurrentUser && <Badge bg="primary">You</Badge>}
                          </div>
                          <Badge bg="primary" className="fs-6 px-3 py-2">
                            {item.totalScore} pts
                          </Badge>
                        </div>

                        <div className="standings-mobile-body">
                          {/* Active Lineup */}
                          <div className="mb-3">
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <span className="fw-bold small text-success">🟢 Active Lineup</span>
                              {isNewRules && <Badge bg="success" style={{ fontSize: '0.65rem' }}>Live {activeRosterSize}</Badge>}
                            </div>
                            <div className="d-flex flex-wrap gap-2 justify-content-start">
                              {item.activeCastaways.map(c => renderCastawayItem(c, false))}
                              {item.activeCastaways.length === 0 && <span className="text-muted small">No active players</span>}
                            </div>
                          </div>

                          {/* Bench Lineup */}
                          {isNewRules && item.benchCastaways.length > 0 && (
                            <div className="mt-2 pt-2 border-top">
                              <div className="d-flex align-items-center gap-2 mb-2">
                                <span className="fw-bold small text-secondary">⏸️ Bench</span>
                                <Badge bg="secondary" style={{ fontSize: '0.65rem' }}>Reserve {rosterSize - activeRosterSize}</Badge>
                              </div>
                              <div className="d-flex flex-wrap gap-2 justify-content-start">
                                {item.benchCastaways.map(c => renderCastawayItem(c, true))}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="standings-mobile-footer d-flex justify-content-between align-items-center flex-wrap gap-2">
                          <div>
                            <span className="text-muted me-1">Elimination:</span>
                            <Badge bg="info" className="text-dark">+{item.eliminationPickPoints} pts</Badge>
                          </div>
                          <div>
                            <span className="text-muted me-1">Survivors:</span>
                            <Badge bg={getSurvivorBadgeColor(item.survivorsRemaining)}>
                              {item.survivorsRemaining}/{rosterSize} left
                            </Badge>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </>
          ) : (
            !isLoading && <Alert variant="secondary">No drafts submitted for this league yet.</Alert>
          )}
          <div className="text-center mt-4">
            <Link to="/" className="btn btn-secondary">Back to Home</Link>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default LeagueStandings;